/**
 * 보기 → [편집] → 고치기 — 상세의 정보 줄(2026-10-20).
 *
 * 화면 상세의 이름·위치·태그는 늘 입력칸·드롭다운으로 펼쳐져 있었다. 읽으러 온 사람에게도 칸이 먼저 보여 값이 눈에
 * 들어오지 않고, 드롭다운을 잘못 건드리면 그대로 저장됐다. 평소에는 값만 보이고 오른쪽 연필 단추를 눌렀을 때만 고치는
 * 칸과 [저장]·[취소] 가 나온다(사용자 지시).
 *
 * **고치는 중인지는 부르는 쪽이 쥔다(`editing`).** 화면 상세는 5초마다 다시 불러오므로 초안을 따로 쥐어야 하고(그래야
 * 타이핑 도중에 덮어쓰이지 않는다), "초안이 있다" 가 곧 "고치는 중" 이다. 저장을 마치면 부르는 쪽이 초안을 비우고, 그러면
 * 보기로 돌아온다.
 *
 * 키: 입력칸에서 Enter 는 저장, Esc 는 취소 — 한글 조합 중의 Enter 는 글자를 끝내는 것이라 저장하지 않는다. 입력칸이
 * 먼저 Enter 를 쓰면(태그 칸은 Enter 가 "태그 추가" 다) `preventDefault` 로 알려 주고, 그때는 저장하지 않는다.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { IconButton } from './IconButton.js';
import { isComposingKey } from './ui.js';

export function EditableField({
  label,
  view,
  canEdit,
  editing,
  saving = false,
  error = null,
  onEdit,
  onSave,
  onCancel,
  children,
}: {
  /** 무엇을 고치는가 — 연필 단추를 "위치 편집" 으로 읽게 한다. */
  label: string;
  /** 평소에 보이는 값. */
  view: ReactNode;
  /** 고칠 권한 — 없으면 연필 단추를 두지 않는다. */
  canEdit: boolean;
  editing: boolean;
  saving?: boolean;
  /** 저장하지 못한 까닭(빈 이름 등) — 칸 밑에 붙는다. */
  error?: string | null;
  /** 연필을 눌렀다 — 부르는 쪽이 지금 값으로 초안을 만든다. */
  onEdit: () => void;
  /** 바뀐 것이 없으면 요청 없이 닫으면 된다 — [저장] 은 늘 누를 수 있다. */
  onSave: () => void;
  onCancel: () => void;
  /** 고치는 칸. */
  children: ReactNode;
}): ReactNode {
  const editRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);
  const wasEditing = useRef(editing);

  /*
   * 연필을 누르면 고치는 칸으로, 저장·취소로 닫으면 다시 연필로 손을 옮긴다 — 키보드로 쓰는 사람이 칸이 사라진 자리에서
   * 길을 잃지 않게. 닫힐 때 손이 이미 다른 곳에 가 있으면(다른 줄을 눌렀다) 끌어오지 않는다.
   */
  useEffect(() => {
    if (editing && !wasEditing.current) {
      editRef.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
    } else if (!editing && wasEditing.current) {
      const active = document.activeElement;
      if (!active || active === document.body) viewRef.current?.querySelector<HTMLElement>('button')?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  if (!editing) {
    return (
      <div ref={viewRef} className="field-view">
        <div className="field-view-value">{view}</div>
        {canEdit ? <IconButton icon={Pencil} label="편집" subject={label} className="field-view-edit" onClick={onEdit} /> : null}
      </div>
    );
  }

  return (
    <div
      ref={editRef}
      className="field-edit"
      onKeyDown={(event) => {
        if (isComposingKey(event.nativeEvent)) return;
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        } else if (event.key === 'Enter' && !event.defaultPrevented && event.target instanceof HTMLInputElement) {
          event.preventDefault();
          onSave();
        }
      }}
    >
      <div className="field-edit-body">{children}</div>
      <span className="field-edit-actions">
        <button type="button" className="btn btn-tiny btn-primary" disabled={saving} onClick={onSave}>
          {saving ? '저장 중…' : '저장'}
        </button>
        <button type="button" className="btn btn-tiny" disabled={saving} onClick={onCancel}>
          취소
        </button>
      </span>
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
